import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Palette } from "lucide-react";
import { FabricThumb } from "./FabricCard";
import { FabricCodePicker } from "./FabricCodePicker";
import { useFabricLibrary } from "./useFabricLibrary";
import { resolveOrderFabrics, parseManualFabrics } from "./fabricUtils";

// Popover opened by double-clicking a FABRIC cell: shows the row's fabrics and lets the user type codes.
export function FabricCellEditor({ orderId, name, content, photos, manual, anchor, onSetManualFabrics, onClose }) {
  const library = useFabricLibrary();
  const resolved = useMemo(() => resolveOrderFabrics(content, library, photos, manual), [content, library, photos, manual]);
  const manualKeys = parseManualFabrics(manual).map((r) => r.key);
  const existingKeys = useMemo(() => new Set(resolved.fabrics.map((f) => f.key)), [resolved]);
  const [, setTick] = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    // Follow the cell when the table scrolls or the layout shifts (banners, row updates)
    const reposition = () => setTick((t) => t + 1);
    const interval = setInterval(reposition, 300);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      clearInterval(interval);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [onClose]);

  const rect = anchor?.isConnected ? anchor.getBoundingClientRect() : { left: 8, top: 8, bottom: 8 };
  const width = 360;
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const top = rect.bottom + 420 < window.innerHeight ? rect.bottom + 4 : Math.max(8, rect.top - 424);

  return createPortal(
    <div className="fixed inset-0 z-[75]" onMouseDown={onClose} data-testid="fabric-cell-editor-overlay">
      <div className="fixed bg-white rounded-xl shadow-2xl border border-zinc-200 p-3" style={{ top, left, width }} onMouseDown={(e) => e.stopPropagation()} data-testid="fabric-cell-editor">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5 truncate"><Palette className="w-4 h-4 text-orange-500 flex-shrink-0" /><span className="truncate">Toiles{name ? ` — ${name}` : ""}</span></p>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-zinc-100" data-testid="fabric-cell-editor-close"><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        {resolved.fabrics.length > 0 ? (
          <div className="space-y-1.5 mb-2 max-h-48 overflow-y-auto">
            {resolved.fabrics.map((f, i) => (
              <div key={f.key} className="flex items-center gap-2" data-testid={`fabric-cell-editor-item-${i}`}>
                <FabricThumb orderId={orderId} fabric={f} className="w-12 h-8" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-zinc-900 tracking-wide leading-tight">{f.code}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{f.collection || "Collection inconnue"} · {f.manual ? "saisie manuelle" : "détectée dans CONTENT"}</p>
                </div>
                {f.manual && (
                  <button type="button" onClick={() => onSetManualFabrics(orderId, manualKeys.filter((k) => k !== f.key))} className="p-1 rounded hover:bg-red-50" title="Retirer cette toile" data-testid={`fabric-cell-editor-remove-${i}`}>
                    <X className="w-3.5 h-3.5 text-red-500" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-zinc-500 mb-2">Aucune toile pour cette commande.</p>
        )}
        <FabricCodePicker library={library} existingKeys={existingKeys} autoFocus testId="fabric-cell-picker"
          onPick={(key) => onSetManualFabrics(orderId, [...new Set([...manualKeys, key])])} />
      </div>
    </div>,
    document.body
  );
}
